/**
 * Session controller — §12.2.
 *
 * Holds who is signed in. §4.3: "On a 401 the SPA renders a single 'Sign in
 * with Google' button pointing at /auth/login", so a 401 is modelled as a
 * state, not an error.
 *
 * Derived values use `createMemo`; `createEffect` is not used here at all.
 */

import { createMemo, createResource, createSignal, type Accessor } from 'solid-js';

import { api, Unauthenticated, type Me } from '~/api/client';

export interface SessionController {
  signals: {
    /** null while loading, then the user or `false` when unauthenticated. */
    user: Accessor<Me | false | null>;
  };
  derivations: {
    isAuthenticated: Accessor<boolean>;
    isLoading: Accessor<boolean>;
    displayName: Accessor<string>;
  };
  actions: {
    refresh: () => void;
    signIn: (returnTo?: string) => void;
    signOut: () => Promise<void>;
    signOutEverywhere: () => Promise<void>;
  };
}

export function createSessionController(): SessionController {
  const [reloadToken, setReloadToken] = createSignal(0);

  const [resource, { refetch }] = createResource(reloadToken, async (): Promise<Me | false> => {
    try {
      return await api.me();
    } catch (error) {
      if (error instanceof Unauthenticated) return false;
      throw error;
    }
  });

  const user = () => (resource.loading && resource() === undefined ? null : (resource() ?? false));

  const isAuthenticated = createMemo(() => {
    const value = user();
    return value !== null && value !== false;
  });

  const isLoading = createMemo(() => user() === null);

  const displayName = createMemo(() => {
    const value = user();
    if (!value) return '';
    return value.name ?? value.email;
  });

  return {
    signals: { user },
    derivations: { isAuthenticated, isLoading, displayName },
    actions: {
      refresh: () => {
        setReloadToken((n) => n + 1);
        void refetch();
      },
      signIn: (returnTo?: string) => {
        const target = returnTo ?? `${window.location.pathname}${window.location.search}`;
        // The server validates `returnTo` again (§4.1); this is convenience,
        // not a control.
        window.location.href = `/auth/login?returnTo=${encodeURIComponent(target)}`;
      },
      signOut: async () => {
        await api.logout().catch(() => undefined);
        window.location.href = '/';
      },
      signOutEverywhere: async () => {
        await api.logoutAll().catch(() => undefined);
        window.location.href = '/';
      },
    },
  };
}
